import React, { useEffect, useState } from 'react';
import { Typography, Stack, Box, Grid, Paper, Button, Container, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, MenuItem, Select, FormControl, InputLabel, Snackbar, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SecurityIcon from '@mui/icons-material/Security';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { products } from '../data/products';
import { useCart } from '../Context/CartContext';

const ProductInfo = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useCart();

    const product = products.find(p => p.id === productId);

    const [selectedModel, setSelectedModel] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleBackClick = () => {
        navigate(-1);
    };

    const handleAddToCart = () => {
        if (product.models && !selectedModel) {
            setSnackbarMessage('Please select a model');
            setSnackbarOpen(true);
            return;
        }

        let modelData = null;
        if (product.models && selectedModel) {
            // Flatten models array to find the selected one
            const allModels = product.models.flatMap(group => group.data);
            modelData = allModels.find(m => m.model === selectedModel);
        }

        addToCart(product, modelData, parseInt(quantity));
        setSnackbarMessage('Item added to cart!');
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    if (!product) {
        return (
            <Container sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h5">Product not found.</Typography>
                <Button onClick={handleBackClick} sx={{ mt: 2 }}>Go Back</Button>
            </Container>
        );
    }

    // Flatten models for the dropdown
    const allModels = product.models ? product.models.flatMap(group => group.data) : [];

    return (
        <Box sx={{ backgroundColor: "#f9f9f9", minHeight: "100vh", py: 4 }}>
            <Container maxWidth="lg">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBackClick}
                    sx={{
                        color: "#7C001F",
                        mb: 3,
                        fontWeight: 600,
                        '&:hover': { backgroundColor: 'rgba(124, 0, 31, 0.05)' }
                    }}
                >
                    Back to Products
                </Button>

                <Paper elevation={0} sx={{ borderRadius: "24px", boxShadow: "0 10px 40px rgba(0,0,0,0.05)" }}>
                    <Grid container>
                        {/* Left Section: Image */}
                        <Grid item xs={12} md={6} sx={{ backgroundColor: "#fff", p: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: { md: '1px solid #f0f0f0' } }}>
                            <Box
                                component="img"
                                src={product.image}
                                alt={product.title}
                                sx={{
                                    width: "100%",
                                    height: "auto",
                                    maxHeight: "500px",
                                    objectFit: "contain",
                                    transition: "transform 0.3s ease",
                                    '&:hover': { transform: "scale(1.02)" }
                                }}
                            />
                        </Grid>

                        {/* Right Section: Product Details */}
                        <Grid item xs={12} md={6} sx={{ p: { xs: 3, md: 5 } }}>
                            <Box mb={3}>
                                <Typography variant="h4" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, color: "#1a1a1a", mb: 1 }}>
                                    {product.title}
                                </Typography>
                                <Typography variant="h6" sx={{ fontFamily: 'Inter, sans-serif', color: "#666", fontWeight: 400 }}>
                                    {product.subtitle}
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            <Typography variant="body1" sx={{ fontFamily: 'Inter, sans-serif', color: "#444", lineHeight: 1.8, mb: 4 }}>
                                {product.description}
                            </Typography>

                            {/* Features Grid */}
                            <Grid container spacing={2} sx={{ mb: 4 }}>
                                <Grid item xs={6}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <SecurityIcon sx={{ color: "#7C001F" }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>1 Year Warranty</Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs={6}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <VerifiedIcon sx={{ color: "#7C001F" }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Govt. Certified</Typography>
                                    </Stack>
                                </Grid>
                                {product.sevenDayDelivery && (
                                    <Grid item xs={6}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <LocalShippingIcon sx={{ color: "#7C001F" }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>7 Day Delivery</Typography>
                                        </Stack>
                                    </Grid>
                                )}
                                <Grid item xs={6}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <EventAvailableIcon sx={{ color: "#7C001F" }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>All Year Service</Typography>
                                    </Stack>
                                </Grid>
                            </Grid>

                            {/* Add to Cart Section */}
                            <Box sx={{ mt: 4 }}>
                                {product.models && (
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel id="model-select-label">Select Model</InputLabel>
                                        <Select
                                            labelId="model-select-label"
                                            value={selectedModel}
                                            label="Select Model"
                                            onChange={(e) => setSelectedModel(e.target.value)}
                                        >
                                            {allModels.map((model, index) => (
                                                <MenuItem key={index} value={model.model}>
                                                    {model.model} - {model.capacity} (Acc: {model.accuracy})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}

                                <Stack direction="row" spacing={2} alignItems="center">
                                    <TextField
                                        type="number"
                                        label="Quantity"
                                        value={quantity}
                                        onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                                        inputProps={{ min: 1 }}
                                        sx={{ width: '100px' }}
                                    />
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        size="large"
                                        onClick={handleAddToCart}
                                        startIcon={<AddShoppingCartIcon />}
                                        sx={{
                                            backgroundColor: "#7C001F",
                                            color: "white",
                                            py: 1.5,
                                            borderRadius: "12px",
                                            fontSize: "16px",
                                            fontWeight: 600,
                                            boxShadow: "0 4px 12px rgba(124, 0, 31, 0.2)",
                                            '&:hover': { backgroundColor: "#5C001F" }
                                        }}
                                    >
                                        Add to Cart
                                    </Button>
                                </Stack>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Specifications Section */}
                    <Box sx={{ p: { xs: 3, md: 5 }, backgroundColor: "#fafafa", borderTop: "1px solid #eee" }}>
                        <Typography variant="h5" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, color: "#1a1a1a", mb: 4 }}>
                            Technical Specifications
                        </Typography>
                        <Grid container spacing={4}>
                            {product.specifications && product.specifications.map((spec, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            backgroundColor: "#fff",
                                            borderRadius: "12px",
                                            border: "1px solid rgba(0,0,0,0.05)",
                                            borderLeft: "4px solid #7C001F", // Brand accent
                                            height: "100%",
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                                            transition: "all 0.3s ease",
                                            '&:hover': {
                                                transform: "translateY(-4px)",
                                                boxShadow: "0 12px 24px rgba(124, 0, 31, 0.1)"
                                            }
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ color: "#7C001F", display: "block", mb: 1, textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600, fontSize: "0.75rem" }}>
                                            {spec.label}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, color: "#222", fontSize: "1.1rem", fontFamily: 'Inter, sans-serif' }}>
                                            {spec.value}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    {/* Models Table Section */}
                    {product.models && product.models.map((modelGroup, groupIndex) => (
                        <Box key={groupIndex} sx={{ p: { xs: 3, md: 5 }, borderTop: "1px solid #eee" }}>
                            <Typography variant="h5" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, color: "#1a1a1a", mb: 4 }}>
                                {modelGroup.title}
                            </Typography>
                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee', borderRadius: '12px' }}>
                                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableRow>
                                            {modelGroup.columns.map((col, index) => (
                                                <TableCell key={index} sx={{ fontWeight: 600, color: '#333', fontFamily: 'Inter, sans-serif' }}>
                                                    {col}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {modelGroup.data.map((row, index) => (
                                            <TableRow
                                                key={index}
                                                sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: '#fafafa' } }}
                                            >
                                                <TableCell component="th" scope="row" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
                                                    {row.model}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: 'Inter, sans-serif' }}>{row.capacity}</TableCell>
                                                <TableCell sx={{ fontFamily: 'Inter, sans-serif' }}>{row.accuracy}</TableCell>
                                                <TableCell sx={{ fontFamily: 'Inter, sans-serif' }}>{row.size}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ))}

                    {/* Additional Details Section */}
                    {product.moreDetails && (
                        <Box sx={{ p: { xs: 3, md: 5 }, borderTop: "1px solid #eee" }}>
                            <Typography variant="h5" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, color: "#1a1a1a", mb: 2 }}>
                                More Details
                            </Typography>
                            <Typography variant="body1" sx={{ fontFamily: 'Inter, sans-serif', color: "#555", lineHeight: 1.8 }}>
                                {product.moreDetails}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Container>
            <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ProductInfo;